import React, { useState, useEffect } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { postAnswer } from "../../api/answer";
import { getQuizById, getQuizIdsByScenario } from "../../api/quiz";
import { getTexts } from "../../api/text";
import NextButton from "../../components/NextButton";
import ScenarioBar from "../../components/ScenarioBar";
import { useTimer } from "../../hooks/useTimer";
import { updateScore, updateTime } from "../../redux/actions";
import { useLang } from "../../translations/i18n";
import { QUIZZES, ROUTES, SCENARIOS, SCORE_TYPES } from "../../utils/constants";
import {
	getScenarioByScreen,
	getTimer,
	updateStats,
} from "../../utils/helpers";
import {
	alertError,
	modalGeneric,
	modalGenericSmall,
} from "../../utils/logger";

const QUIZ_ID = QUIZZES.SCENARIO6;
const SCENARIO_ID = SCENARIOS.SCENARIO_6;
const NAVIGATE_TO = ROUTES.SCENARIO6_END;

const WRONG_ANSWER_POINTS = -10;
const MAX_WRONG_ANSWER_POINTS = -50;

const DEFAULT_FILTER1 = "";
const DEFAULT_FILTER2 = "";
const DEFAULT_FILTER3 = "";

const DEFAULT_OPTIONS = [
	{ id: 1, selected: [false, false, false], correct: 2, text: "Reducir la dosis de voclosporina en 7,9 mg (1 cápsula) 2 veces al día. Volver a hacer la prueba en dos semanas; si la disminución de la TFGe no se ha recuperado, volver a reducir la dosis en 7,9 mg (1 cápsula) 2 veces al día. " },
	{ id: 2, selected: [false, false, false], correct: 3, text: "Mantener la dosis actual de voclosporina y supervisar." },
	{ id: 3, selected: [false, false, false], text: "Cambiar voclosporina por otro inhibidor de la calcineurina." },
	{ id: 4, selected: [false, false, false], correct: 1, text: "Suspender la administración de voclosporina y reiniciar el tratamiento tras la recuperación de la TFGe con una dosis de 7,9 mg (1 cápsula) 2 veces al día y aumentar la dosis según la tolerabilidad de la función renal. " },
	{ id: 5, selected: [false, false, false], text: "Repetir la TFGe dentro de 2 semanas y reducir la dosis en 6,5-13 mg BID. " },
];

function shuffleArray(arr) {
	const a = [...arr];
	for (let i = a.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[a[i], a[j]] = [a[j], a[i]];
	}
	return a;
}

function Scenario6({ intl }) {
	const lang = useLang();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const participant = useSelector((state) => state.group);
	const cachedQuiz = useSelector((state) => state.quizCache?.[SCENARIO_ID]);

	const scenarioNum = getScenarioByScreen(location.pathname);

	const [points, setPoints] = useState({ penalty: 0 });
	const [time, setTime] = useState(0);

	const [isCompleted, setIsCompleted] = useState(false);

	const [pageTitle, setPageTitle] = useState("");
	const [pageContent, setPageContent] = useState("");
	const [recommendationTitle, setRecommendationTitle] = useState("");
	const [modalFinal, setModalFinal] = useState("");

	useTimer(
		() => {
			setTime(time + 1);
		},
		isCompleted ? null : 1000
	);

	const [filters, setFilters] = useState([
		{ id: 1, text: "", selected: true },
		{ id: 2, text: "", selected: false },
		{ id: 3, text: "", selected: false },
	]);

	const [options, setOptions] = useState([]);

	useEffect(() => {
		const gameId = participant?.game;
		if (!gameId) return;
		getTexts({ game: gameId })
			.then((res) => {
				if (res.status === 200 && Array.isArray(res.data)) {
					const map = res.data.reduce((acc, t) => ({ ...acc, [t.key]: t.value }), {});
					if (map["scenario6.title"] != null && map["scenario6.title"] !== "") setPageTitle(map["scenario6.title"]);
					if (map["scenario6.content"] != null && map["scenario6.content"] !== "") setPageContent(map["scenario6.content"]);
					if (map["scenario6.recommendation-title"] != null && map["scenario6.recommendation-title"] !== "") setRecommendationTitle(map["scenario6.recommendation-title"]);
					if (map["scenario6.modal-final"] != null && map["scenario6.modal-final"] !== "") setModalFinal(map["scenario6.modal-final"]);
				}
			})
			.catch(() => {});
	}, [participant?.game]);

	const [resolvedQuizId, setResolvedQuizId] = useState(null);

	function applyQuizData(data) {
		if (!data) return;
		const raw = data.filterLabels ?? data.filter_labels ?? (data.data && (data.data.filterLabels ?? data.data.filter_labels));
		const labels = Array.isArray(raw) ? raw : null;
		if (labels && labels.length >= 1) {
			setFilters([
				{ id: 1, text: (labels[0] && String(labels[0]).trim()) ?? DEFAULT_FILTER1, selected: true },
				{ id: 2, text: (labels[1] && String(labels[1]).trim()) ?? DEFAULT_FILTER2, selected: false },
				{ id: 3, text: (labels[2] && String(labels[2]).trim()) ?? DEFAULT_FILTER3, selected: false },
			]);
		}
		const opts = data.options ?? data.data?.options;
		if (opts && Array.isArray(opts) && opts.length > 0) {
			const mapped = opts.map((o) => {
				const filterId = typeof o.explanation === "string" && /^[123]$/.test(o.explanation) ? Number(o.explanation) : undefined;
				return {
					id: o.id,
					text: o.text || "",
					correct: filterId,
					selected: [false, false, false],
				};
			});
			setOptions(shuffleArray(mapped));
		} else {
			setOptions(shuffleArray(DEFAULT_OPTIONS.map((o) => ({ ...o, selected: [false, false, false] }))));
		}
	}

	useEffect(() => {
		const gameId = participant?.game;
		if (!gameId) return;

		if (cachedQuiz?.data) {
			setResolvedQuizId(cachedQuiz.resolvedQuizId || null);
			applyQuizData(cachedQuiz.data);
			return;
		}

		let cancelled = false;
		const params = { game: gameId };
		getQuizIdsByScenario(SCENARIO_ID, params)
			.then((listRes) => {
				if (cancelled) return;
				const list = listRes?.data && Array.isArray(listRes.data) ? listRes.data : [];
				const id = list[0]?._id || QUIZ_ID;
				setResolvedQuizId(id);
				return getQuizById(id);
			})
			.then((res) => {
				if (cancelled) return;
				applyQuizData(res?.data);
				if (!res?.data) setOptions(shuffleArray(DEFAULT_OPTIONS.map((o) => ({ ...o, selected: [false, false, false] }))));
			})
			.catch(() => {
				if (!cancelled) setOptions(shuffleArray(DEFAULT_OPTIONS.map((o) => ({ ...o, selected: [false, false, false] }))));
			});
		return () => { cancelled = true; };
	}, [participant?.game, cachedQuiz?.data]);

	const SelectFilterOption = ({ id }) => {
		let filter = filters.find((x) => x.id === id);

		function handleOptionClick() {
			if (!isCompleted) return;
			let updatedFilters = filters.map((x) =>
				x.id === id
					? { ...x, selected: true }
					: { ...x, selected: false }
			);

			setFilters(updatedFilters);
		}

		function getBackgroundColor() {
			return filter?.selected ? "#FF7F6B" : "#FFFFFF";
		}

		return (
			<div>
				<div
					className={`d-flex flex-column align-items-center justify-content-center txt-dark-green mt-3 p-4 ${
						filter?.selected ? "option-selected txt-white" : ""
					}`}
					style={{
						height: "fit-content",
						lineHeight: "20px",
						borderRadius: "4px",
						backgroundColor: getBackgroundColor(),
					}}
					onClick={handleOptionClick}
				>
					<div className="txt20 bold">
						{filter?.text && filter.text.indexOf("<") >= 0 ? (
							<span dangerouslySetInnerHTML={{ __html: filter.text }} />
						) : (
							filter?.text
						)}
					</div>
				</div>
			</div>
		);
	};

	const SelectOption = ({ id, displayIndex }) => {
		let option = options.find((x) => x.id === id);

		const index = filters?.findIndex((x) => x.selected);

		const selected = option?.selected[index];

		function handleOptionClick() {
			if (isCompleted) return;

			let updatedOptions = options.map((x) => {
				if (x.id === id) {
					let updatedSelected = [...x.selected];
					if (selected) updatedSelected[index] = false;
					else updatedSelected[index] = filters[index]?.id;

					return {
						...x,
						selected: updatedSelected,
					};
				} else {
					let updatedSelected = [...x.selected];
					updatedSelected[index] = false;
					return {
						...x,
						selected: updatedSelected,
					};
				}
			});

			setOptions(updatedOptions);
		}

		function getBackgroundColor() {
			return selected && option.correct === selected && isCompleted
				? "#D6D431"
				: selected && option.correct !== selected && isCompleted
				? "#FF5948"
				: selected
				? "#FF7F6B"
				: "#FFFFFF";
		}

		return (
			<div>
				<div
					className={`d-flex align-items-center justify-content-start txt-black mt-3 p-3 ${
						selected ? "option-selected txt-white bold" : ""
					}`}
					style={{
						height: "fit-content",
						lineHeight: "20px",
						borderRadius: "4px",
						backgroundColor: getBackgroundColor(),
					}}
					onClick={handleOptionClick}
				>
					<div className="d-flex align-items-start justify-content-start">
						<div>{displayIndex != null ? displayIndex + 1 : option?.id}.</div>
						<div className="ms-2">
							{option?.text && option.text.indexOf("<") >= 0 ? (
								<span dangerouslySetInnerHTML={{ __html: option.text }} />
							) : (
								option?.text
							)}
						</div>
					</div>
				</div>
			</div>
		);
	};

	function checkAnswers() {
		const currentFilter = filters.find((x) => x.selected);
		const activeIndex = filters.findIndex((x) => x.selected);

		if (options?.every((x) => !x.selected[activeIndex])) {
			alertError({
				error: " ",
				customMessage: "Para avanzar debes seleccionar una opción.",
			});
			return;
		} else if (
			!options?.some((x) => x.selected[activeIndex] === x.correct)
		) {
			const text = `<div class="text-center">
			<p class="txt-turquoise bold">La recomendación es incorrecta.</p>
			<p class="txt-black">Inténtalo de nuevo</p>
		</div>`;

			setPoints({
				...points,
				penalty: points?.penalty + WRONG_ANSWER_POINTS,
			});

			modalGenericSmall({
				html: text,
				confirmButtonText: "INTENTAR DE NUEVO",
				allowOutsideClick: true,
			});
		} else if (activeIndex < filters.length - 1) {
			const text = `<div class="text-center"><p class="txt-turquoise bold">¡Correcto!</p><p class="txt-black">¡A por la siguiente!</p></div>`;

			modalGenericSmall({
				html: text,
				confirmButtonText: "AVANZAR",
				allowOutsideClick: false,
			}).then((res) => {
				if (res.isConfirmed) {
					const updatedFilters = filters.map((filter) => {
						if (filter.id === currentFilter.id) {
							return { ...filter, selected: false };
						} else if (filter.id == currentFilter.id + 1)
							return { ...filter, selected: true };
						else return filter;
					});

					setFilters(updatedFilters);
				}
			});
		} else {
			modalGeneric({ html: modalFinal }).then((res) => {
				if (res.isConfirmed) submitAnswer();
			});
		}
	}

	async function submitAnswer() {
		setIsCompleted(true);

		const penaltyScore = Math.max(points.penalty, MAX_WRONG_ANSWER_POINTS);

		let totalTime = getTimer();
		dispatch(updateTime(totalTime, 1));
		postAnswer({
			scenario: SCENARIO_ID,
			quiz: resolvedQuizId ?? QUIZ_ID,
			group: participant?._id,
			score: penaltyScore,
			time: time,
			correct: true,
		})
			.then((res) => {
				if (res.status === 201) {
					dispatch(updateScore(penaltyScore, SCORE_TYPES.SCORE));
					dispatch(updateScore(penaltyScore, SCORE_TYPES.PENALTY));

					updateStats({
						...participant,
						time: totalTime,
						score: participant?.score + penaltyScore,
						penalty: participant?.penalty + penaltyScore,
						lastVisited: NAVIGATE_TO,
					});
				}
			})
			.catch((error) => {});
	}

	return (
		<div className="h-100 bg-prueba">
			<div className="container">
				<div className="row h-100">
					<div className="d-flex justify-content-center align-items-start">
						<div className="col-3">
							<div className="txtWhite txt20 bold">
								{pageTitle && pageTitle.indexOf("<") >= 0 ? (
									<span dangerouslySetInnerHTML={{ __html: pageTitle }} />
								) : (
									pageTitle
								)}
							</div>
							{filters.map((filter) => {
								return (
									<div>
										<SelectFilterOption id={filter.id} />
									</div>
								);
							})}
							<div className="txt8 mt-5 txt-white" style={{ whiteSpace: "pre-line" }}>
								{pageContent && pageContent.indexOf("<") >= 0 ? (
									<span dangerouslySetInnerHTML={{ __html: pageContent.replace(/\n/g, "<br />") }} />
								) : (
									pageContent
								)}
							</div>
						</div>
						<div className="col-1"></div>
						<div className="col-8">
							<div>
								<div className="txtWhite txt20 bold">
									{recommendationTitle && recommendationTitle.indexOf("<") >= 0 ? (
										<span dangerouslySetInnerHTML={{ __html: recommendationTitle }} />
									) : (
										recommendationTitle
									)}
								</div>
								{options.length === 0 ? (
									<div className="txt-white mt-3">Cargando opciones...</div>
								) : (
									options.map((option, index) => (
										<div key={option.id}>
											<SelectOption id={option.id} displayIndex={index} />
										</div>
									))
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
			<div
				className="container"
				style={{
					position: "absolute",
					right: "4vh",
					bottom: "17vh",
					zIndex: 1000,
				}}
			>
				<div className="d-flex justify-content-end">
					{!isCompleted ? (
						<div
							className="btn-main"
							onClick={() => checkAnswers()}
						>
							<div className="text-btn-main">AVANZAR</div>
						</div>
					) : (
						<NextButton navigateTo={NAVIGATE_TO} />
					)}
				</div>
			</div>
			<ScenarioBar activeScenario={scenarioNum}></ScenarioBar>
		</div>
	);
}

export default injectIntl(Scenario6);
